'use client';

import { useState, useMemo, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import { encodeFunctionData, type Hex } from 'viem';
import { Check, LogOut, AlertTriangle, Coins } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useTransactionQueue } from '@/hooks/use-transactions';
import { useWallet } from '@/hooks/use-wallet';
import { UGM_ABI, YIELD_ADAPTER_WITHDRAW_ABI } from '@/lib/contracts';
import { cn, parseGridId } from '@/lib/utils';
import type { V2GridAsset } from '@/lib/types/v2';

interface WithdrawPositionCardProps {
  gridId: string;
  assets: V2GridAsset[];
  managerAddress: string;
}

export function WithdrawPositionCard({
  gridId,
  assets,
  managerAddress,
}: WithdrawPositionCardProps) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { executeActions } = useTransactionQueue();
  const { wallet, address } = useWallet();
  const [selectedAssets, setSelectedAssets] = useState<Set<string>>(new Set());
  const [localSubmitting, setLocalSubmitting] = useState(false);

  const activeAssets = useMemo(() => assets.filter(a => a.active), [assets]);

  const toggleAsset = useCallback((assetId: string) => {
    setSelectedAssets(prev => {
      const next = new Set(prev);
      if (next.has(assetId)) {
        next.delete(assetId);
      } else {
        next.add(assetId);
      }
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    if (selectedAssets.size === activeAssets.length) {
      setSelectedAssets(new Set());
    } else {
      setSelectedAssets(new Set(activeAssets.map(a => a.id)));
    }
  }, [selectedAssets.size, activeAssets]);

  const handleWithdraw = useCallback(async () => {
    if (!wallet || !address || selectedAssets.size === 0) return;

    setLocalSubmitting(true);
    const mgr = managerAddress as Hex;
    const gid = BigInt(parseGridId(gridId).numericId);

    try {
      const steps: Array<{
        id: string;
        label: string;
        description: string;
        call: { to: Hex; data: Hex };
      }> = [];

      for (const assetId of selectedAssets) {
        const asset = activeAssets.find(a => a.id === assetId);
        if (!asset?.adapterAddress) {
          console.error(`[WithdrawPositionCard] missing adapterAddress for asset ${assetId}`);
          continue;
        }

        steps.push({
          id: `withdraw-asset-${assetId.slice(0, 8)}`,
          label: `Withdraw Asset`,
          description: `Withdraw NFT from adapter`,
          call: {
            to: asset.adapterAddress as Hex,
            data: encodeFunctionData({
              abi: YIELD_ADAPTER_WITHDRAW_ABI,
              functionName: 'withdraw',
              args: [assetId as Hex, address as Hex],
            }),
          },
        });
      }

      const allAssetsSelected = selectedAssets.size === activeAssets.length;
      if (allAssetsSelected) {
        steps.push({
          id: 'creator-close-grid',
          label: 'Close Grid',
          description: 'Refund deposits, collect fees, permanently close grid',
          call: {
            to: mgr,
            data: encodeFunctionData({
              abi: UGM_ABI,
              functionName: 'creatorCloseGrid',
              args: [gid],
            }),
          },
        });
      }

      await executeActions('Withdraw Position', steps, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['v2-grid', gridId] });
          queryClient.invalidateQueries({ queryKey: ['wallet'] });
          if (allAssetsSelected) router.push('/');
        },
      });
    } catch (error) {
      console.error('[WithdrawPositionCard] withdraw failed:', error);
    } finally {
      setLocalSubmitting(false);
    }
  }, [wallet, address, selectedAssets, activeAssets, managerAddress, gridId, executeActions, queryClient, router]);

  const allSelected = selectedAssets.size === activeAssets.length && activeAssets.length > 0;
  const canSubmit = !localSubmitting && wallet && address && selectedAssets.size > 0;

  return (
    <Card className="border-red-500/30">
      <CardHeader className="p-3 pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <LogOut className="h-4 w-4 text-red-400" />
          Withdraw Position
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-0 space-y-2.5">
        <p className="text-xs text-muted-foreground">
          You own all 100 tiles. Select assets to withdraw, then close the grid. Deposits will be refunded and pending fees claimed.
        </p>

        {activeAssets.length > 0 ? (
          <>
            {activeAssets.length > 1 && (
              <button
                onClick={toggleAll}
                className="text-xs text-primary hover:underline"
              >
                {allSelected ? 'Deselect all' : 'Select all'}
              </button>
            )}
            <div className="max-h-[200px] overflow-y-auto border border-border rounded-lg select-none">
              {activeAssets.map(asset => {
                const isChecked = selectedAssets.has(asset.id);
                return (
                  <div
                    key={asset.id}
                    className={cn(
                      "flex items-center gap-2 px-2 py-2 border-b border-border last:border-b-0 transition-colors cursor-pointer",
                      isChecked ? "bg-red-500/10" : "hover:bg-muted/50"
                    )}
                    onClick={() => toggleAsset(asset.id)}
                  >
                    <div
                      className={cn(
                        "w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors",
                        isChecked
                          ? "bg-red-500 border-red-500"
                          : "border-muted-foreground/30"
                      )}
                    >
                      {isChecked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                    </div>
                    <div className="w-6 h-6 rounded-md bg-muted/50 border border-border/50 flex items-center justify-center shrink-0 overflow-hidden">
                      {asset.iconUrl ? (
                        <img src={asset.iconUrl} alt={asset.name || 'Asset'} className="w-full h-full object-cover" />
                      ) : (
                        <Coins className="h-3 w-3 text-muted-foreground" />
                      )}
                    </div>
                    <span className="text-sm font-medium truncate flex-1">
                      {asset.name || asset.symbol || `Asset ${asset.id.slice(0, 8)}`}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="text-xs text-muted-foreground text-center py-2">
            No active assets to withdraw
          </div>
        )}

        <div className="bg-amber-500/10 border border-amber-500/20 rounded-md p-2 flex items-start gap-2">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-[11px] text-amber-200/80">
            This will permanently close the grid. All 100 tiles will be cleared.
          </p>
        </div>

        <Button
          onClick={handleWithdraw}
          disabled={!canSubmit}
          className="w-full bg-red-500 hover:bg-red-600 text-white"
          size="sm"
        >
          {localSubmitting
            ? 'Processing...'
            : selectedAssets.size === 0
              ? 'Select Assets to Withdraw'
              : selectedAssets.size === activeAssets.length
                ? `Withdraw ${selectedAssets.size} Asset${selectedAssets.size !== 1 ? 's' : ''} & Close Grid`
                : `Withdraw ${selectedAssets.size} Asset${selectedAssets.size !== 1 ? 's' : ''}`
          }
        </Button>
      </CardContent>
    </Card>
  );
}
